import { useState } from "react";
import { useNavigate } from "react-router-dom";

//img
import Modal from "./Modal";
import Popup from "./Popup";
import constants from "../../utils/constants";
import logo from "../../assets/icons/logo.svg";
import fundraisin_logo_dark from "../../assets/images/fundraisin_logo_dark.svg";
import fundraisin_logo_light from "../../assets/images/fundraisin_logo_light.svg";
import creator_tools_logo_dark from "../../assets/images/creator_tools_logo_dark.svg";
import beta_white from "../../assets/images/beta_white.svg"
import { useWallet } from "../../context/WalletContextProvider";
import { useGlobal } from "../../context/GlobalContextProvider";
import { useError } from "../../context/ErrorContextProvider";

// Top Component
export default function Top() {

  const { account, connectWallet, walletBalance, sendTransaction } = useWallet();
  const { userType, campaign, artPack, setSpinner, setArtPack, setCampaign } = useGlobal();
  const { showError, showSuccess, showInfo } = useError();
  // State Variables
  const [showBuildPopup, setShowBuildPopup] = useState(false); // Controls build popup visibility
  const [tempAddress, setTempAddress] = useState(); // Temporary deployment address
  const [tempAddrBalance, setTempAddressBalance] = useState(0); // Balance of temporary address
  const navigate = useNavigate();

  // Handles redirection after checking campaign or artPack selection
  const checkStatusAfterPreview = async () => {
    if (userType === "sponsor" && campaign?.campaign_id) {
      // Sponsor navbar preview - show full site with header/footer
      sessionStorage.setItem('previewSource', 'sponsor');
      sessionStorage.setItem('previewContext', 'navbar');
      sessionStorage.setItem('previewReturnUrl', window.location.pathname);
      navigate(`/preview-home`);
    }
    else if (userType === "artist" && artPack?.art_pack_id) {
      // Artist navbar preview - show only mint page without header/footer
      sessionStorage.setItem('previewSource', 'artist');
      sessionStorage.setItem('previewContext', 'navbar');
      sessionStorage.setItem('previewReturnUrl', window.location.pathname);
      navigate(`/preview-home/mint`)
    }
    else {
      showError(
        `Please, choose ${userType === "sponsor" ? "a Campaign" : "an Art Pack"
        }`
      );
    }
  };

  const fetchAddress = async (account) => {
    const response = await fetch(`/api/wallet/${account}/build/address`);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error fetching address: ${errorData?.error || 'Unknown error'}`);
    }
    return response.json();
  }

  const updateBalance = async (address) => {
    const balance = await walletBalance(address);
    setTempAddressBalance(balance);
    return balance;
  }

  const needsMoreBalance = (balance, userType) => {
    const requiredValue = userType === "sponsor"
      ? constants.CAMPAIGN_BUILD_VALUE
      : constants.ART_BUILD_VALUE;

    return balance < Number(requiredValue);
  }

  const waitForSufficientBalance = (address, userType) => {
    const intervalId = setInterval(async () => {
      const currentBalance = await walletBalance(address);

      if (!needsMoreBalance(currentBalance, userType)) {
        setSpinner(false);
        setTempAddressBalance(currentBalance);
        clearInterval(intervalId); // Stop the interval
      }
    }, 5000);
  }

  const checkAndUpdateBalance = async (account, userType) => {
    try {
      setSpinner(true);
      const { id, address } = await fetchAddress(account);
      sessionStorage.setItem(constants.TEMP_ADDRESS_KEY, id);
      setTempAddress(address);

      const initialBalance = await updateBalance(address);

      if (needsMoreBalance(initialBalance, userType)) {
        waitForSufficientBalance(address, userType);
      }

      setSpinner(false);
      setShowBuildPopup(true);
    } catch (error) {
      setSpinner(false);
      showError(`Error creating temporary wallet`);
      console.error('Error during balance check:', error);
    }
  }

  // Initiates build process by checking balance, creating temporary address, and opening popup
  const openBuild = async () => {
    const allowed = ((userType === "sponsor" && !campaign?.sc_id) ||
      (userType === "artist" && (!artPack?.status || !artPack?.approved)))
    if (allowed) {
      if (campaign || artPack) {
        try {
          await checkAndUpdateBalance(account, userType);
        } catch (err) {
          console.error(err);
        }
      } else {
        showInfo(
          `Please, choose ${userType === "sponsor" ? "a Campaign" : "an Art Pack"
          } from the list to build.`
        );
      }
    } else {
      showInfo(
        `${userType === "sponsor" ? "Campaign" : "Art Pack"} already built.`
      );
    }
  };

  // Transfers the required AVAX balance to the temporary address for deployment
  const handleTransfer = async () => {
    try {
      setSpinner(true);
      const required =
        Number(
          userType === "sponsor"
            ? constants.CAMPAIGN_BUILD_VALUE
            : constants.ART_BUILD_VALUE
        );
      await sendTransaction(tempAddress, required.toString());
    } catch (err) {
      setSpinner(false);
      console.error("Error during transfer:", err);
    }
  }

  // Closes the build popup and clears the timer
  const closeBuild = () => {
    setShowBuildPopup(false);
  };

  // Initiates build process for campaign if userType is sponsor
  const handleBuildCampaign = async () => {
    if (campaign) {
      const tempAddress = sessionStorage.getItem(constants.TEMP_ADDRESS_KEY);
      try {
        const req = await fetch(`/api/build/campaign/${campaign?.campaign_id}`, {
          method: "POST",
          body: JSON.stringify({
            wid: tempAddress,
          }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });
        if (!req.ok) {
          const errorData = await req.json();
          const errorMessage = `${errorData?.error}`;
          showError(`We encountered an issue, but your AVAX has been returned.\n\nCampaign build failed: ${errorMessage}`);
          setSpinner(false);
          setShowBuildPopup(false);
        } else {
          const res = await req.json();
          if (Object.keys(res).length > 0) {
            showSuccess(`Congratulations, your Campaign has been successfully built! Now in artist queue for approval.`);
            setShowBuildPopup(false);
            setSpinner(false);
            setCampaign({
              ...campaign,
              sc_id: res?.sc_id,
            })
          }
        }
      } catch (err) {
        setSpinner(false);
      }
    } else {
      setSpinner(false);
    }
  };

  // Initiates build process for art if userType is artist
  const handleBuildArt = async () => {
    if (artPack?.art_pack_id > 0) {
      const tempAddress = sessionStorage.getItem(constants.TEMP_ADDRESS_KEY);
      try {
        const req = await fetch(`/api/build/art/${artPack?.art_pack_id}`, {
          method: "POST",
          body: JSON.stringify({
            wid: tempAddress,
          }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });
        if (req?.ok) {
          const res = await req.json();
          if (Object.keys(res).length > 0) {
            showSuccess(`Congratulations, your Art Pack has been successfully built! Now anyone can use your Art Pack for supporting their campaign.`);
            setShowBuildPopup(false);
            setSpinner(false);
            setArtPack({
              ...artPack,
              sc_id: res?.sc_id,
              status: true,
              approved: true
            })
          }
        } else {
          const errorData = await req.json();
          const errorMessage = `${errorData?.error}`;
          showError(`We encountered an issue, but your AVAX has been returned.\n\nArt Pack Failed: ${errorMessage}`);
          setSpinner(false);
          setShowBuildPopup(false);
        }

      } catch (err) {
        console.log(err);
        setSpinner(false);
      }
    } else {
      setSpinner(false);
    }
  };

  // Selects appropriate build handler based on user type
  const handleBuild = async () => {
    setSpinner(true);
    if (userType === "sponsor") {
      handleBuildCampaign();
    } else {
      handleBuildArt();
    }
  };

  // Determines content for build popup, depending on whether balance is sufficient
  const enableBuildContent = () => {
    if (
      Number(tempAddrBalance) >=
      Number(
        userType === "sponsor"
          ? constants.CAMPAIGN_BUILD_VALUE
          : constants.ART_BUILD_VALUE
      )
    ) {
      return (
        <div className="row justify-content-center modal-build-text">
          You're all set to build the
          {userType === "sponsor"
            ? ` campaign ${campaign?.campaign_name}`
            : ` art ${artPack?.art_pack_name}`}
        </div>
      );
    } else {
      return (
        <div className="row justify-content-center modal-build-text">
          Request you to transfer
          {` ${Number(
            userType === "sponsor"
              ? constants.CAMPAIGN_BUILD_VALUE
              : constants.ART_BUILD_VALUE
          )} `}
          AVAX to the deployment address {tempAddress}.
          <br />
          Once deployed, the remaining AVAX will be transferred back to your
          wallet
        </div>
      );
    }
  };

  // Determines button for build popup, depending on whether balance is sufficient
  const enableBuildButton = () => {
    if (
      Number(tempAddrBalance) >=
      Number(
        userType === "sponsor"
          ? constants.CAMPAIGN_BUILD_VALUE
          : constants.ART_BUILD_VALUE
      )
    ) {
      return (
        <button className="btn bg-orange form-btn" onClick={handleBuild}>
          Build
        </button>
      );
    } else {
      return (
        <button className="btn bg-orange form-btn" onClick={handleTransfer}>
          Send
        </button>
      );
    }
  };

  // Renders the build address popup with conditional button and content
  const renderBuildAddressPopup = () => {
    return (
      <Modal show={showBuildPopup}>
        <Popup>
          {enableBuildContent()}
          <div className="row col-sm-12 d-flex justify-content-between mt-4">
            <div className="col-sm-12 d-flex justify-content-center mt-4">
              <button
                className="btn bg-orange form-btn me-3"
                onClick={closeBuild}
              >
                Discard
              </button>
              &nbsp;
              {enableBuildButton()}
            </div>
          </div>
        </Popup>
      </Modal>
    );
  };

  // JSX for Top Component
  return (
    <>
      {/* TOP */}
      <nav className={`navbar navbar-expand-lg`}>
        <div className="container-fluid">
          <div className="logo-wrapper">
            <img src={logo} className="logo" onClick={() => navigate(`/`)} alt="img" />
          </div>
          <div className="right-nav-wrapper">
            <div className={`col-sm-${["artist", "sponsor"].includes(userType) ? "6" : "10"}`}>
              {["artist", "sponsor"].includes(userType) ? (
                <div className="home-nav-logos">
                  <img src={fundraisin_logo_light} className="logo-title-fundraisin" alt="fundraisin logo" />
                  <span className="creator-tools-text">{userType === "artist" ? "Creator Tools for Artist" : "Creator Tools for Sponsor"}</span>
                  <img className="logo_beta_white ms-3" src={beta_white} alt="logo beta fundraisin" />
                </div>
              ) : (
                <div className="home-nav-logos">
                  <img src={fundraisin_logo_dark} className="logo-title-fundraisin" alt="fundraisin logo" />
                  <img src={creator_tools_logo_dark} className="logo-title-creator-tools" alt="creator tools logo" />
                  <img className="logo_beta_white ms-3" src={beta_white} alt="logo beta fundraisin" />
                
                </div>
              )}

            </div>
            <div className={`col-sm-${["artist", "sponsor"].includes(userType) ? "6" : "2"} text-success`}>

              <div className="container">
                <div className="row justify-content-sm-end align-items-center">
                  {!account ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary wallet-btn"
                      onClick={connectWallet}
                    >
                      Connect your wallet
                    </button>
                  ) : (
                    <>
                      {userType && (
                        <>
                          <button
                            className="btn wallet-btn me-3"
                            style={{
                              cursor:
                                ((userType === "sponsor" && campaign?.campaign_id) ||
                                  (userType === "artist" && artPack?.art_pack_id))
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            onClick={checkStatusAfterPreview}
                          >
                            Preview
                          </button>
                          <button
                            className="btn wallet-btn me-3"
                            style={{
                              cursor:
                                ((userType === "sponsor" && !campaign?.sc_id) ||
                                  (userType === "artist" && (!artPack?.status || !artPack?.approved)))
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            onClick={openBuild}
                          >
                            Build
                          </button>
                        </>
                      )}

                      <div className="btn-top">
                        <button className="btn btn-outline-primary wallet-btn">
                          {`${account.slice(
                            0,
                            6
                          )}...${account.slice(
                            account.length - 4
                          )}`}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      {renderBuildAddressPopup()}
    </>
  );
}
